import { FC } from "react";

import { Box, Stack } from "@mui/material";
import { Theme } from "@mui/material/styles";

import { isChromatic } from "@/common/helpers/isChromatic";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

interface AssistantThinkingIndicatorProps {
  label: string;
}

const DOTS: Array<{ top: string; left: string; color: (theme: Theme) => string }> = [
  { top: "9%", left: "36%", color: (theme) => theme.palette.secondary.main },
  { top: "55%", left: "64%", color: (theme) => theme.palette.text.brand.quiet },
  { top: "55%", left: "9%", color: (theme) => theme.palette.text.neutral.intense },
];

export const AssistantThinkingIndicator: FC<AssistantThinkingIndicatorProps> = ({ label }) => {
  const isAnimated = !isChromatic();
  return (
    <Stack direction="row" spacing={1} alignItems="center" role="status" sx={{ pr: 12 }}>
      <Stack
        alignItems="center"
        justifyContent="center"
        aria-hidden
        sx={(theme) => ({
          flexShrink: 0,
          width: theme.spacing(5),
          height: theme.spacing(5),
          borderRadius: "50%",
          border: `1px solid ${theme.palette.border.brand.quiet}`,
          bgcolor: theme.palette.surface.neutral.white,
        })}
      >
        <Box
          sx={(theme) => ({
            position: "relative",
            width: theme.spacing(2.75),
            height: theme.spacing(2.75),
            transform: "rotate(27deg)",
            "@keyframes assistant-thinking-spin": {
              from: { transform: "rotate(27deg)" },
              to: { transform: "rotate(387deg)" },
            },
            "@keyframes assistant-thinking-jump": {
              "0%, 60%, 100%": { translate: "0 0" },
              "30%": { translate: `0 -${theme.spacing(0.25)}` },
            },
            ...(isAnimated && { animation: "assistant-thinking-spin 1.6s linear infinite" }),
            "@media (prefers-reduced-motion: reduce)": { animation: "none", "& > *": { animation: "none" } },
          })}
        >
          {DOTS.map((dot, index) => (
            <Box
              key={index}
              sx={(theme) => ({
                position: "absolute",
                top: dot.top,
                left: dot.left,
                width: theme.spacing(0.75),
                height: theme.spacing(0.75),
                borderRadius: "50%",
                bgcolor: dot.color(theme),
                ...(isAnimated && {
                  animation: "assistant-thinking-jump 0.8s ease-in-out infinite",
                  animationDelay: `${index * 0.15}s`,
                }),
              })}
            />
          ))}
        </Box>
      </Stack>
      <Stack
        sx={(theme) => ({
          px: 1.5,
          py: 1,
          borderRadius: theme.radius(1),
          bgcolor: theme.palette.surface.neutral.supershy,
        })}
      >
        <DashboardTypography variant="body2" vibe="text-neutral-intense">
          {label}
        </DashboardTypography>
      </Stack>
    </Stack>
  );
};
